/**
 * Centralized Storage Utility
 * 
 * Provides type-safe localStorage access with consistent key management.
 * All localStorage operations should go through this module to:
 * 1. Prevent typos in storage keys
 * 2. Enable easy refactoring (e.g., switch to sessionStorage)
 * 3. Add encryption/compression if needed in the future
 * 4. Improve testability with easy mocking
 */

import type { ResponseUser } from '@/types'

/**
 * Centralized storage keys - single source of truth
 * Using 'as const' ensures type safety and autocomplete
 */
const STORAGE_KEYS = {
  TOKEN: 'token',
  USER: 'user',
  THEME: 'kura-ui-theme',
  TOKEN_EXPIRATION: 'token-expiration', // ISO 8601 datetime string
} as const

/**
 * Storage utility object with type-safe methods
 */
export const storage = {
  // ========================================
  // TOKEN OPERATIONS
  // ========================================

  /**
   * Get JWT authentication token from localStorage
   * @returns JWT token string or null if not found
   */
  getToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.TOKEN)
  },

  /**
   * Store JWT authentication token in localStorage
   * @param token - JWT token string to store
   */
  setToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.TOKEN, token)
  },

  /**
   * Remove JWT token from localStorage
   */
  removeToken(): void {
    localStorage.removeItem(STORAGE_KEYS.TOKEN)
  },

  /**
   * Get token expiration time from localStorage
   * @returns ISO 8601 datetime string or null if not found
   */
  getTokenExpiration(): string | null {
    return localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRATION)
  },

  /**
   * Store token expiration time in localStorage
   * @param expirationTime - ISO 8601 datetime string from backend
   */
  setTokenExpiration(expirationTime: string): void {
    localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRATION, expirationTime)
  },

  /**
   * Remove token expiration time from localStorage
   */
  removeTokenExpiration(): void {
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRATION)
  },

  /**
   * Check if the stored token is expired
   * @returns true if token is expired or expiration time is not found
   */
  isTokenExpired(): boolean {
    const token = this.getToken()
    const expirationTime = this.getTokenExpiration()
    
    // If no token exists at all, don't consider it "expired" - it's just not authenticated
    // This prevents false warnings when user is logged out
    if (!token) {
      return false
    }

    // If token exists but no expiration time, consider it expired (shouldn't happen)
    if (!expirationTime) {
      return true
    }

    try {
      const expirationDate = new Date(expirationTime)
      const now = new Date()
      
      // Add a 30-second buffer to handle clock skew and network delays
      const bufferMs = 30 * 1000
      return now.getTime() + bufferMs >= expirationDate.getTime()
    } catch (error) {
      console.error('Failed to parse token expiration time:', error)
      return true // If we can't parse it, consider it expired
    }
  },

  // ========================================
  // USER OPERATIONS
  // ========================================

  /**
   * Get current user data from localStorage
   * @returns Parsed user object or null if not found/invalid
   */
  getUser(): ResponseUser | null {
    const userStr = localStorage.getItem(STORAGE_KEYS.USER)
    
    // Handle missing or invalid data
    if (!userStr || userStr === 'undefined' || userStr === 'null') {
      return null
    }

    try {
      return JSON.parse(userStr) as ResponseUser
    } catch (error) {
      // If JSON parsing fails, remove the corrupted data
      console.error('Failed to parse user data from localStorage:', error)
      localStorage.removeItem(STORAGE_KEYS.USER)
      return null
    }
  },

  /**
   * Store user data in localStorage
   * @param user - User object to store
   */
  setUser(user: ResponseUser): void {
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user))
  },

  /**
   * Remove user data from localStorage
   */
  removeUser(): void {
    localStorage.removeItem(STORAGE_KEYS.USER)
  },

  // ========================================
  // THEME OPERATIONS
  // ========================================

  /**
   * Get theme preference from localStorage
   * @returns Theme string ('light', 'dark', 'system') or null
   */
  getTheme(): string | null {
    return localStorage.getItem(STORAGE_KEYS.THEME)
  },

  /**
   * Store theme preference in localStorage
   * @param theme - Theme value to store
   */
  setTheme(theme: string): void {
    localStorage.setItem(STORAGE_KEYS.THEME, theme)
  },

  /**
   * Remove theme preference from localStorage
   */
  removeTheme(): void {
    localStorage.removeItem(STORAGE_KEYS.THEME)
  },

  // ========================================
  // BULK OPERATIONS
  // ========================================

  /**
   * Clear all authentication-related data (token + user)
   * Use this for logout operations
   */
  clearAuth(): void {
    this.removeToken()
    this.removeTokenExpiration()
    this.removeUser()
  },

  /**
   * Clear all application data from localStorage
   * Use with caution - this removes everything including theme
   */
  clearAll(): void {
    this.removeToken()
    this.removeTokenExpiration()
    this.removeUser()
    this.removeTheme()
  },

  /**
   * Check if user is authenticated (has valid token and user data)
   * @returns true if both token and user exist in localStorage
   */
  hasAuth(): boolean {
    return !!(this.getToken() && this.getUser() && !this.isTokenExpired())
  },
}

/**
 * Export storage keys for reference if needed
 * (e.g., for storage event listeners)
 */
export const STORAGE_KEY_NAMES = STORAGE_KEYS
